// src/pages/task/TaskEditPage.tsx
// Full-featured task editing — mirrors TaskCreatePage layout with checklists,
// watchers, custom fields, type definitions, and tags.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useTaskTaskType, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ErrorAlert,
  Alert,
} from '@react/components';
import {
  taskTypeOptions,
  syncTagsForEntity,
  loadEntityTags,
  getInitials,
  getAvatarColor,
  getDisplayName,
  findTaskTypeDefinition,
  validateTaskForm,
} from '@/features/task/logic';
import type { TaskTypeDefinition, TaskChecklistItem } from '@core/types/task';
import type { Tag } from '@core/types/tag';
import { Select } from '@/components/Select';
import { PrioritySelect } from '@/components/PrioritySelect';
import { UserPicker } from '@/components/UserPicker';
import { TagPicker } from '@/components/TagPicker';
import { CustomFieldsCard } from '@/components/task/CustomFieldsCard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ─── Icons ───
const ICONS = {
  plus: 'M12 5v14m-7-7h14',
  x: 'M18 6L6 18M6 6l12 12',
  checklist: 'M9 11l3 3L22 4 M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11',
  externalLink: 'M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6 M15 3h6v6 M10 14L21 3',
} as const;

function Icon({ d, className = 'h-4 w-4' }: { d: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      {d.split(' M').map((seg, i) => (
        <path key={i} d={i === 0 ? seg : `M${seg}`} />
      ))}
    </svg>
  );
}

function ManageLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors" target="_blank">
      <Icon d={ICONS.externalLink} className="h-3 w-3" />
      {children}
    </Link>
  );
}

// ─── Checklist draft editor (for new checklists to be added) ───
interface ChecklistDraft {
  title: string;
  items: { id: string; content: string; is_done: boolean }[];
}

function ChecklistEditor({ checklists, onChange }: {
  checklists: ChecklistDraft[];
  onChange: (checklists: ChecklistDraft[]) => void;
}) {
  const { t } = useI18n();

  const addChecklist = () => {
    onChange([...checklists, { title: t('task.checklist'), items: [] }]);
  };
  const removeChecklist = (idx: number) => onChange(checklists.filter((_, i) => i !== idx));
  const updateTitle = (idx: number, title: string) => {
    const next = [...checklists];
    next[idx] = { ...next[idx], title };
    onChange(next);
  };
  const addItem = (clIdx: number) => {
    const next = [...checklists];
    next[clIdx] = { ...next[clIdx], items: [...next[clIdx].items, { id: crypto.randomUUID(), content: '', is_done: false }] };
    onChange(next);
  };
  const updateItem = (clIdx: number, itemIdx: number, content: string) => {
    const next = [...checklists];
    next[clIdx] = { ...next[clIdx], items: next[clIdx].items.map((it, i) => i === itemIdx ? { ...it, content } : it) };
    onChange(next);
  };
  const toggleItem = (clIdx: number, itemIdx: number) => {
    const next = [...checklists];
    next[clIdx] = { ...next[clIdx], items: next[clIdx].items.map((it, i) => i === itemIdx ? { ...it, is_done: !it.is_done } : it) };
    onChange(next);
  };
  const removeItem = (clIdx: number, itemIdx: number) => {
    const next = [...checklists];
    next[clIdx] = { ...next[clIdx], items: next[clIdx].items.filter((_, i) => i !== itemIdx) };
    onChange(next);
  };

  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, clIdx: number, itemIdx: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem(clIdx);
      setTimeout(() => {
        const inputs = document.querySelectorAll(`[data-checklist="${clIdx}"] input[type="text"]`);
        (inputs[inputs.length - 1] as HTMLInputElement)?.focus();
      }, 50);
    }
    if (e.key === 'Backspace' && (e.target as HTMLInputElement).value === '') {
      e.preventDefault();
      removeItem(clIdx, itemIdx);
    }
  };

  return (
    <div className="space-y-3">
      {checklists.map((cl, clIdx) => (
        <div key={clIdx} data-checklist={clIdx} className="rounded-md border border-border">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
            <Icon d={ICONS.checklist} className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <input
              type="text"
              value={cl.title}
              onChange={(e) => updateTitle(clIdx, e.target.value)}
              className="flex-1 text-sm font-medium text-foreground bg-transparent border-0 outline-none placeholder:text-muted-foreground"
              placeholder={t('task.checklistTitle')}
            />
            <button type="button" onClick={() => removeChecklist(clIdx)} className="text-muted-foreground hover:text-destructive transition-colors">
              <Icon d={ICONS.x} className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="px-3 py-2 space-y-1">
            {cl.items.map((item, itemIdx) => (
              <div key={item.id} className="flex items-center gap-2 group">
                <input
                  type="checkbox"
                  checked={item.is_done}
                  onChange={() => toggleItem(clIdx, itemIdx)}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring flex-shrink-0"
                />
                <input
                  type="text"
                  value={item.content}
                  onChange={(e) => updateItem(clIdx, itemIdx, e.target.value)}
                  onKeyDown={(e) => handleItemKeyDown(e, clIdx, itemIdx)}
                  placeholder={t('task.checklistItemPlaceholder')}
                  className={`flex-1 text-sm bg-transparent border-0 outline-none placeholder:text-muted-foreground ${
                    item.is_done ? 'line-through text-muted-foreground' : 'text-foreground'
                  }`}
                />
                <button type="button" onClick={() => removeItem(clIdx, itemIdx)} className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">
                  <Icon d={ICONS.x} className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addItem(clIdx)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              <Icon d={ICONS.plus} className="h-3 w-3" />
              {t('task.addChecklistItem')}
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addChecklist}
        className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
      >
        <Icon d={ICONS.checklist} className="h-3.5 w-3.5" />
        {t('task.addChecklist')}
      </button>
    </div>
  );
}

function extractDateValue(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toISOString().split('T')[0];
  } catch {
    return '';
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════

interface WatcherRef {
  id: string;
  name: string;
}

export const TaskEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTask();
  const taskTypeStore = useTaskTaskType();
  const { t } = useI18n();
  const scope = useScope();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [descriptionTab, setDescriptionTab] = useState<'write' | 'preview'>('write');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [initialTags, setInitialTags] = useState<Tag[]>([]);
  const [parentProjectId, setParentProjectId] = useState('');
  const [parentTeamId, setParentTeamId] = useState('');
  const [newChecklists, setNewChecklists] = useState<ChecklistDraft[]>([]);
  const [watchers, setWatchers] = useState<WatcherRef[]>([]);
  const [initialWatcherIds, setInitialWatcherIds] = useState<string[]>([]);
  const [activeTypeDef, setActiveTypeDef] = useState<TaskTypeDefinition | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [existingChecklistCount, setExistingChecklistCount] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'TASK_TYPE_TASK',
    priority: 'TASK_PRIORITY_MEDIUM',
    assignee: '',
    due_date: '',
    start_date: '',
    estimated_hours: '',
  });

  const [initialFormData, setInitialFormData] = useState(formData);

  // ─── Load data ───
  useEffect(() => {
    if (!id) return;
    store.GetTask(id);
    loadEntityTags(id, 'task').then((tags) => {
      setSelectedTags(tags);
      setInitialTags(tags);
    });
    return () => {
      store.clearCurrent();
    };
  }, [id, scope.productId]);

  // Load task type definitions
  useEffect(() => {
    taskTypeStore.ListTaskTypes({ pagination: { limit: 100, cursor: '', direction: 'forward' }, sort: [] } as any);
  }, []);

  // Resolve active type definition when type changes — from the loaded
  // TaskTypeDefinition list (ListTaskTypes), not a hardcoded enum→slug table
  // (kept in sync with the same helper TaskCreatePage uses), so custom task
  // types get their defaults and feature flags too.
  useEffect(() => {
    setActiveTypeDef(findTaskTypeDefinition(taskTypeStore.items || [], formData.type));
  }, [formData.type, taskTypeStore.items]);

  // Populate form from loaded task
  useEffect(() => {
    if (!store.currentItem) return;
    const item = store.currentItem as any;
    const task = item.task || {};

    const estimatedSecMatch = task.estimated_duration ? String(task.estimated_duration).match(/^(\d+)/) : null;
    const estimatedHours = estimatedSecMatch ? String(parseInt(estimatedSecMatch[1]) / 3600) : '';

    const next = {
      title: item.title ?? '',
      description: item.description ?? '',
      type: item.type ? String(item.type) : 'TASK_TYPE_TASK',
      priority: task.priority ? String(task.priority) : 'TASK_PRIORITY_MEDIUM',
      assignee: task.assignee?.entity?.id ?? '',
      due_date: extractDateValue(task.due_date),
      start_date: extractDateValue(task.start_date),
      estimated_hours: estimatedHours,
    };
    setFormData(next);
    setInitialFormData(next);

    if (item.parent?.id) {
      const parentType = item.parent.type_name || '';
      if (parentType === 'team') setParentTeamId(item.parent.id);
      else setParentProjectId(item.parent.id);
    }

    // Existing watchers → initial list for diffing
    const actors = task.watchers?.actors || [];
    const loaded: WatcherRef[] = actors.map((a: any) => ({
      id: a.entity?.id || '',
      name: getDisplayName(a),
    })).filter((w: WatcherRef) => w.id);
    setWatchers(loaded);
    setInitialWatcherIds(loaded.map((w: WatcherRef) => w.id));

    if (next.start_date || next.estimated_hours) setShowMore(true);

    const existingChecklists = item.checklists?.checklists || task.checklists?.checklists || [];
    setExistingChecklistCount(existingChecklists.length);
  }, [store.currentItem]);

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => { if (!prev[field]) return prev; const next = { ...prev }; delete next[field]; return next; });
  }, []);

  // ─── Submit ───
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    const validationErrors = validateTaskForm(formData);
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    setIsSubmitting(true);

    try {
      // Build diff-aware field_mask so we only overwrite what actually changed.
      const paths: string[] = [];
      const taskPatch: any = { task: {} };

      if (formData.title !== initialFormData.title) {
        taskPatch.title = formData.title.trim();
        paths.push('title');
      }
      if (formData.description !== initialFormData.description) {
        taskPatch.description = formData.description.trim();
        paths.push('description');
      }
      if (formData.type !== initialFormData.type) {
        taskPatch.type = formData.type;
        paths.push('type');
      }
      if (formData.priority !== initialFormData.priority) {
        taskPatch.task.priority = formData.priority;
        paths.push('task.priority');
      }
      if (formData.assignee !== initialFormData.assignee) {
        if (formData.assignee) {
          taskPatch.task.assignee = { entity: { id: formData.assignee } };
        }
        paths.push('task.assignee');
      }
      if (formData.due_date !== initialFormData.due_date) {
        if (formData.due_date) {
          taskPatch.task.due_date = new Date(formData.due_date).toISOString();
        }
        paths.push('task.due_date');
      }
      if (formData.start_date !== initialFormData.start_date) {
        if (formData.start_date) {
          taskPatch.task.start_date = new Date(formData.start_date).toISOString();
        }
        paths.push('task.start_date');
      }
      if (formData.estimated_hours !== initialFormData.estimated_hours) {
        const h = parseFloat(formData.estimated_hours);
        if (h > 0) {
          taskPatch.task.estimated_duration = `${Math.floor(h * 3600)}s`;
        }
        paths.push('task.estimated_duration');
      }

      if (paths.length > 0) {
        await store.UpdateTask(id, {
          task: taskPatch,
          field_mask: paths,
        } as any);
      }

      // New checklists — add each individually.
      for (const cl of newChecklists) {
        const items = cl.items
          .filter((it) => it.content.trim())
          .map((it, i) => ({ content: it.content, is_done: it.is_done, order: i } as TaskChecklistItem));
        if (items.length === 0 && !cl.title.trim()) continue;
        await store.AddChecklist(id, {
          task: { id },
          checklist: {
            title: cl.title,
            items: { items } as any,
          } as any,
        } as any);
      }

      // Watchers diff
      const currentWatcherIds = watchers.map((w) => w.id);
      const toAdd = currentWatcherIds.filter((wid) => !initialWatcherIds.includes(wid));
      const toRemove = initialWatcherIds.filter((wid) => !currentWatcherIds.includes(wid));
      for (const wid of toAdd) {
        await store.AddTaskWatcher(id, {
          task: { id },
          watcher: { entity: { id: wid } },
        } as any);
      }
      for (const wid of toRemove) {
        await store.RemoveTaskWatcher(id, wid);
      }

      // Tags
      await syncTagsForEntity(initialTags, selectedTags, id, 'task');

      navigate(`/tasks/${id}`);
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Loading ───
  if ((store.isLoading || store.isLoadingItem) && !store.currentItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 w-full max-w-2xl">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // ─── Error — GetTask failed (previously fell through to a blank form) ───
  if (store.error && !store.currentItem) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/tasks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  // ─── Not found ───
  if (!store.currentItem) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/tasks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const taskTitle = (store.currentItem as any)?.title ?? '';
  const enableChecklists = activeTypeDef?.enable_checklists !== false;
  const enableWatchers = activeTypeDef?.enable_watchers !== false;
  const requiresAssignee = activeTypeDef?.requires_assignee === true;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/tasks" className="text-muted-foreground hover:text-foreground transition-colors">{t('task.list')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to={`/tasks/${id}`} className="text-muted-foreground hover:text-foreground transition-colors">
              {taskTitle || t('task.details')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('common.edit')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('task.edit')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('task.updateInfo')}</p>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Two-column layout */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">

        {/* ──── Left column: Content ──── */}
        <div className="xl:col-span-2 space-y-4">
          <Card>
            <CardContent className="space-y-4">
              <Input
                label={t('task.field.title')}
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={t('task.field.titlePlaceholder')}
                error={errors.title ? t(errors.title, { field: t('task.field.title'), min: 2, max: 200 }) : undefined}
                required
              />

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-foreground">{t('task.field.description')}</label>
                  <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/30 p-0.5">
                    {(['write', 'preview'] as const).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setDescriptionTab(tab)}
                        className={`px-2.5 py-0.5 text-xs font-medium rounded transition-colors ${
                          descriptionTab === tab ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {t(`task.${tab}`)}
                      </button>
                    ))}
                  </div>
                </div>
                {descriptionTab === 'write' ? (
                  <div>
                    <textarea
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[160px] resize-y font-mono"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder={t('task.field.descriptionPlaceholder')}
                      rows={6}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('task.markdownSupported')}</p>
                  </div>
                ) : (
                  <div className="rounded-md border border-input bg-background px-3 py-2 min-h-[160px]">
                    {formData.description.trim() ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{formData.description}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground/40">{t('task.previewNoDescription')}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Checklists — new drafts added here are saved on submit. */}
          {enableChecklists && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{t('task.checklists')}</CardTitle>
                  {existingChecklistCount > 0 && (
                    <Link
                      to={`/tasks/${id}`}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      {String(t('task.manageExistingChecklists', { count: existingChecklistCount }) || `${existingChecklistCount} existing — manage on detail page`)}
                    </Link>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ChecklistEditor checklists={newChecklists} onChange={setNewChecklists} />
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Link to={`/tasks/${id}`}>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting || !formData.title.trim()} isLoading={isSubmitting}>
              {t('common.saveChanges')}
            </Button>
          </div>
        </div>

        {/* ──── Right column: Properties ──── */}
        <div className="xl:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">{t('task.properties')}</CardTitle>
                <ManageLink to="/tasks/settings">{t('common.settings')}</ManageLink>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Type */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-foreground">{t('task.field.type')}</label>
                  <ManageLink to="/settings/task-types">{t('common.manage')}</ManageLink>
                </div>
                <Select value={formData.type} onChange={(e) => handleChange('type', e.target.value)}>
                  {taskTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </Select>
                {activeTypeDef && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {activeTypeDef.enable_time_tracking && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Time tracking</span>
                    )}
                    {activeTypeDef.enable_checklists && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Checklists</span>
                    )}
                    {activeTypeDef.enable_dependencies && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Dependencies</span>
                    )}
                    {activeTypeDef.requires_assignee && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/20 text-warning">Assignee required</span>
                    )}
                  </div>
                )}
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.field.priority')}</label>
                <PrioritySelect
                  value={formData.priority}
                  onSelect={(val) => handleChange('priority', val)}
                  className="w-full"
                />
              </div>

              {/* Assignee */}
              <UserPicker
                value={formData.assignee}
                onChange={(userId) => handleChange('assignee', userId)}
                label={t('task.field.assignee')}
                placeholder={t('task.field.assigneePlaceholder')}
                error={requiresAssignee && !formData.assignee ? t('task.assigneeRequired') : undefined}
                projectId={parentProjectId || undefined}
                teamId={parentTeamId || undefined}
              />

              {/* Due Date */}
              <Input
                label={t('task.field.dueDate')}
                type="date"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
              />

              {/* Tags */}
              <TagPicker
                label={t('task.field.tags')}
                value={selectedTags}
                onChange={setSelectedTags}
                placeholder={t('tag.selectTags')}
              />

              {/* Watchers */}
              {enableWatchers && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.watchers')}</label>
                  {watchers.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {watchers.map((w) => (
                        <span key={w.id} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
                          <span
                            className="h-4 w-4 rounded-full flex items-center justify-center text-[8px] font-medium text-white flex-shrink-0"
                            style={{ backgroundColor: getAvatarColor(w.name) }}
                          >
                            {getInitials(w.name)}
                          </span>
                          {w.name}
                          <button
                            type="button"
                            onClick={() => setWatchers((prev) => prev.filter((x) => x.id !== w.id))}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Icon d={ICONS.x} className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <UserPicker
                    value=""
                    onChange={(userId, displayName) => {
                      if (userId && !watchers.find((w) => w.id === userId)) {
                        setWatchers((prev) => [...prev, { id: userId, name: displayName }]);
                      }
                    }}
                    placeholder={t('task.addWatcher')}
                    projectId={parentProjectId || undefined}
                    teamId={parentTeamId || undefined}
                  />
                </div>
              )}

              {/* More fields toggle */}
              {!showMore ? (
                <button
                  type="button"
                  onClick={() => setShowMore(true)}
                  className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1.5 font-medium"
                >
                  <Icon d={ICONS.plus} className="h-3.5 w-3.5" />
                  {t('task.moreFields')}
                </button>
              ) : (
                <>
                  <Input
                    label={t('task.startDate')}
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                  />
                  <Input
                    label={t('task.estimatedHours')}
                    type="number"
                    value={formData.estimated_hours}
                    onChange={(e) => handleChange('estimated_hours', e.target.value)}
                    placeholder="0"
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Custom fields — self-managing card (loads + saves on its own). */}
          {id && (
            <CustomFieldsCard
              taskId={id}
              editable
            />
          )}

          {/* Quick links */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 px-1">
            <ManageLink to="/task-templates">{t('task.manageTemplates')}</ManageLink>
            <ManageLink to="/settings/task-types">{t('task.manageTypes')}</ManageLink>
            <ManageLink to="/tasks/settings">{t('task.manageStatuses')}</ManageLink>
          </div>
        </div>
      </form>
    </div>
  );
});

export default TaskEditPage;
